import { Button, Form, Input, Modal, Upload } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd/es/form";
import { AppStrings } from "../../../../../utils/constants/AppStrings";

type AddNewProviderPageProps = {
  open: boolean;
  title: string;
  submitText: string;
  onCancel: () => void;
  onSubmit: () => void;
  saving: boolean;
  form: FormInstance<{ name: string; active: boolean }>;
  formActive: boolean;
  previewUrl: string;
  currentIconUrl: string;
  fallbackProviderIcon: string;
  onBeforeUpload: (file: File) => boolean;
  onRemoveIcon: () => void;
  canUploadImages?: boolean;
  onActiveChange: (value: boolean) => void;
};

function AddNewProviderPage({
  open,
  title,
  submitText,
  onCancel,
  onSubmit,
  saving,
  form,
  formActive,
  previewUrl,
  currentIconUrl,
  fallbackProviderIcon,
  onBeforeUpload,
  onRemoveIcon,
  canUploadImages = true,
  onActiveChange,
}: AddNewProviderPageProps) {
  return (
    <Modal
      open={open}
      rootClassName="casino-providers-modal-root"
      title={<span className="casino-providers-modal-title">{title}</span>}
      className="casino-providers-modal"
      closeIcon={<CloseOutlined />}
      onCancel={onCancel}
      onOk={onSubmit}
      okText={submitText}
      cancelText={AppStrings.CASINO_CONFIG_PROVIDERS_ACTION_CANCEL}
      confirmLoading={saving}
      width={560}
      okButtonProps={{ className: "casino-providers-primary-btn" }}
      cancelButtonProps={{ className: "casino-providers-outline-btn" }}
    >
      <Form form={form} layout="vertical" initialValues={{ active: true }}>
        <Form.Item
          className="casino-providers-form-item"
          label={
            <span className="casino-providers-form-label">
              {AppStrings.CASINO_CONFIG_PROVIDERS_FORM_NAME_LABEL}
            </span>
          }
          name="name"
          rules={[
            {
              required: true,
              message: AppStrings.CASINO_CONFIG_PROVIDERS_FORM_NAME_REQUIRED,
            },
          ]}
        >
          <Input
            className="casino-providers-modal-input"
            placeholder={AppStrings.CASINO_CONFIG_PROVIDERS_FORM_NAME_PLACEHOLDER}
            maxLength={80}
          />
        </Form.Item>

        <div className="casino-providers-icon-title">
          {AppStrings.CASINO_CONFIG_PROVIDERS_FORM_ICON_LABEL}
        </div>
        <div className="casino-providers-config-upload">
          <img
            className="casino-providers-config-upload-preview"
            src={previewUrl || currentIconUrl || fallbackProviderIcon}
            alt={AppStrings.CASINO_CONFIG_PROVIDERS_ICON_PREVIEW_ALT}
          />

          <div>
            <div className="casino-providers-config-upload-actions">
              <Upload
                showUploadList={false}
                beforeUpload={onBeforeUpload}
                accept=".png,.svg,.webp,image/png,image/svg+xml,image/webp"
                disabled={!canUploadImages}
              >
                <Button
                  className="casino-providers-outline-btn"
                  disabled={!canUploadImages}
                >
                  {AppStrings.CASINO_CONFIG_PROVIDERS_FORM_REPLACE}
                </Button>
              </Upload>
              <Button
                className="casino-providers-outline-btn"
                disabled={!canUploadImages}
                onClick={onRemoveIcon}
              >
                {AppStrings.CASINO_CONFIG_PROVIDERS_FORM_DELETE}
              </Button>
            </div>
            <div className="casino-providers-config-upload-note">
              {AppStrings.CASINO_CONFIG_PROVIDERS_FORM_ICON_HINT}
            </div>
          </div>
        </div>

        <div className="casino-providers-active-row">
          <div>
            <div className="casino-providers-form-label">
              {AppStrings.CASINO_CONFIG_PROVIDERS_FORM_ACTIVE_LABEL}
            </div>
            <div className="casino-providers-config-form-helper">
              {AppStrings.CASINO_CONFIG_PROVIDERS_FORM_ACTIVE_HINT}
            </div>
          </div>
          <Form.Item
            className="casino-providers-active-switch"
            name="active"
            valuePropName="checked"
            hidden
          />
          <label className="casino-providers-toggle-switch">
            <input
              type="checkbox"
              checked={Boolean(formActive)}
              onChange={(event) => onActiveChange(event.target.checked)}
              aria-label={AppStrings.CASINO_CONFIG_PROVIDERS_FORM_ACTIVE_LABEL}
            />
            <span className="casino-providers-toggle-slider"></span>
          </label>
        </div>
      </Form>
    </Modal>
  );
}

export default AddNewProviderPage;
